import { DateTime } from 'luxon';
import { TicketingFragment, TicketFragment } from '@graphqlTypes';
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';

interface TicketingContextType {
  // State
  dateOpt: string;
  otherDate: string | null;
  timesLoading: boolean;
  isFutureDate: boolean;
  dateCopy: string | null;
  today: string;
  canSubmit: boolean;
  sessions: TicketureSessionTime[];
  hasSessions: boolean;
  lastSearchDate: string | null;
  selectedSession: string;
  selectedTickets: Record<string, number>;
  infoMessage: string | null;
  groupPricing: boolean;
  ticketsParsed: Record<string, number>;

  // Actions
  handleDateChange: (value: string, isFutureDate?: boolean) => Promise<void>;
  setIsFutureDate: (isFutureDate: boolean) => void;
  setSelectedSession: (sessionId: string) => void;
  fetchSessionTimes: () => Promise<void>;
  updateTickets: (id: string, count: number) => void;
  getDate: () => string;
  resetForm: () => void;
  
  // Labels
  data: TicketingFragment['ticketingForm'];
}

// Create the context
const TicketingContext = createContext<TicketingContextType | undefined>(undefined);

// Hook to use the context
export const useTicketing = () => {
  const context = useContext(TicketingContext);
  if (!context) {
    throw new Error('useTicketing must be used within a TicketingProvider');
  }
  return context;
};

// Provider props
interface TicketingProviderProps {
  children: React.ReactNode;
  data?: TicketingFragment['ticketingForm'];
}

// Provider component
export const TicketingProvider: React.FC<TicketingProviderProps> = ({ 
  children, 
  data = {} 
}) => {
  const [dateOpt, setDateOpt] = useState<string>('');
  const [otherDate, setOtherDate] = useState<string | null>(null);
  const [timesLoading, setTimesLoading] = useState(false);
  const [isFutureDate, setIsFutureDate] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TicketureSessionTime[]>([]);
  const [lastSearchDate, setLastSearchDate] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string>("");
  const [selectedTickets, setSelectedTickets] = useState<Record<string, number>>({});

  const today = DateTime.now().setZone('America/New_York').toISODate();

  const hasSessions = useMemo(() => sessions.length > 0, [sessions]);
  
  const dateCopy = useMemo(() => {
    if (otherDate) return DateTime.fromISO(otherDate).toFormat('MMMM dd, yyyy');
    return null;
  }, [otherDate, isFutureDate, dateOpt]);

  const canSubmit = useMemo(() => {
    if (!selectedSession) return false;
    const totalTickets = Object.values(selectedTickets).reduce((sum, count) => sum + count, 0);
    return totalTickets > 0;
  }, [selectedSession, selectedTickets]);

  const groupPricing = useMemo(() => {
    const { step3Tickets } = data;
    const mainTickets = [...Object.keys(selectedTickets)].filter(id => step3Tickets?.nodes.some(t => t.__typename === "TicketPrice" && t.ticketPriceFields?.ticketing?.ticketureId === id));
    const totalTickets = mainTickets.reduce((sum, id) => sum + (selectedTickets[id] || 0), 0);
    return totalTickets >= 15;
  }, [selectedTickets]);

  const ticketsParsed = useMemo(() => {
    const { step3Tickets } = data;
    const tickets: Record<string, number> = {};
    Object.entries(selectedTickets).forEach(([key, value]) => {
      const ticket = step3Tickets?.nodes.find(t => t.__typename === "TicketPrice" && t.ticketPriceFields?.ticketing?.ticketureId === key) as TicketFragment;
      if (!ticket) {
        tickets[key] = value;
        return;
      };
      const { ticketureId } = ticket.ticketPriceFields.ticketing;
      let id = ticketureId;
      const groupTicket = ticket.ticketPriceFields?.groupTicketPrice;
      if (groupTicket && groupTicket.node.__typename === "TicketPrice" && groupPricing) {
        id = groupTicket.node.ticketPriceFields?.ticketing?.ticketureId || id;
      }
      tickets[id] = value;
    });
    return tickets;
  }, [selectedTickets]);

  const getDate = useCallback(() => {
    switch (dateOpt) {
      case 'today':
        return today;
      case 'tomorrow':
        return DateTime.fromISO(today).plus({ days: 1 }).toISODate();
      default:
        return otherDate ? DateTime.fromISO(otherDate).toISODate() : today;
    }
  }, [otherDate, dateOpt, today]);

  const resetForm = useCallback(() => {
    setDateOpt('');
    setOtherDate(null);
    setIsFutureDate(false);
    setSessions([]);
    setLastSearchDate(null);
    setSelectedSession("");
    setSelectedTickets({});
    setInfoMessage(null);
  }, []);

  const fetchSessionTimes = useCallback(async () => {
    setTimesLoading(true);
    let date = null;
    try {
      date = getDate();
      const { data } = await fetch('/api/tickets/start-session/', { method: "POST", body: JSON.stringify({ date }) }).then(res => res.json());
      setSessions(data.sessions || []);
      setInfoMessage(data.info_message || null);
    } catch (error) {
      console.error('Error fetching session times:', error);
    } finally {
      setLastSearchDate(date);
      setTimesLoading(false);
    }
  }, [getDate]);

  const handleDateChange = useCallback(async (value: string, isFutureDate = false) => {
    setIsFutureDate(isFutureDate);
    if (isFutureDate) {
      setOtherDate(value);
    } else {
      setOtherDate(null);
    }
    setDateOpt(value);
  }, []);

  const updateTickets = (id: string, count: number) => {
    setSelectedTickets(prev => ({ ...prev, [id]: count }));
  };

  useEffect(() => {
    if (dateOpt) {
      fetchSessionTimes();
    }
  }, [dateOpt, fetchSessionTimes]);

  const contextValue: TicketingContextType = {
    // State
    dateOpt,
    otherDate,
    timesLoading,
    isFutureDate,
    dateCopy,
    today,
    canSubmit,
    sessions,
    lastSearchDate,
    selectedSession,
    selectedTickets,
    infoMessage,
    groupPricing,
    ticketsParsed,
    hasSessions,

    // Actions
    handleDateChange,
    fetchSessionTimes,
    setIsFutureDate,
    setSelectedSession,
    updateTickets,
    resetForm,
    getDate,
    
    // Labels
    data,
  };

  return (
    <TicketingContext.Provider value={contextValue}>
      {children}
    </TicketingContext.Provider>
  );
};

export default TicketingProvider;